/* RD Customz — product page module (only loaded on product templates).
   <rdc-product>: projection / pack selection resolves the real Shopify variant from a compact server-rendered map and updates
   price, stock, labels, media, URL and add-to-cart without a network round trip (MP06 §48/§150).
   <rdc-gallery>: scroll-snap gallery with counter, thumbnails and zoom dialog — no slider library (MP06 §45). */
const { RDC, track, announce, openDialog, addToCart } = window.rdc;

class RdcGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rdc-gallery-track]');
    this.counter = this.querySelector('[data-rdc-gallery-counter]');
    this.thumbs = this.querySelector('[data-rdc-gallery-thumbs]');
    this.zoomDialog = this.querySelector('[data-rdc-zoom-dialog]');
    if (!this.track) return;
    this.index = 0;
    this.sync();
    this.track.addEventListener('scroll', () => {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        if (this.track.scrollWidth <= this.track.clientWidth + 2) return;
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (i !== this.index) { this.setIndex(i, false); track('gallery_image_viewed', { image_index: i + 1 }); }
      });
    }, { passive: true });
    this.thumbs?.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;
      const li = btn.closest('li');
      const i = this.visibleThumbs().indexOf(li);
      if (i >= 0) this.setIndex(i, true);
    });
    this.track.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-rdc-zoom]');
      if (btn) this.zoom(btn);
    });
    this.zoomDialog?.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-rdc-zoom-toggle]');
      if (toggle) this.toggleZoom(toggle, e);
    });
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.setIndex(Math.min(this.index + 1, this.visibleSlides().length - 1), true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.setIndex(Math.max(this.index - 1, 0), true); }
    });
  }

  visibleSlides() { return [...this.track.children].filter((n) => !n.hidden); }
  visibleThumbs() { return this.thumbs ? [...this.thumbs.children].filter((n) => !n.hidden) : []; }

  sync() {
    const slides = this.visibleSlides();
    slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
    this.visibleThumbs().forEach((t, i) => {
      const b = t.querySelector('button');
      if (i === this.index) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
      b.setAttribute('aria-label', b.getAttribute('aria-label').replace(/\d+/, String(i + 1)));
    });
    if (this.counter) {
      this.counter.textContent = `${this.index + 1}/${slides.length}`;
      this.counter.hidden = slides.length < 2;
    }
    if (this.thumbs) this.thumbs.hidden = slides.length < 2;
  }

  setIndex(i, scroll) {
    this.index = i;
    const slide = this.visibleSlides()[i];
    if (scroll && slide && this.track.scrollWidth > this.track.clientWidth + 2) {
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    this.sync();
  }

  /* Show the photo of the selected projection design first, hide the previously selected design photo. */
  showDesignMedia(mediaId) {
    if (!mediaId) return;
    const slide = this.track.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    const thumb = this.thumbs?.querySelector(`[data-media-id="${mediaId}"]`);
    if (slide.dataset.design === 'true') {
      this.track.querySelectorAll('[data-design="true"]').forEach((s) => { s.hidden = s !== slide; });
      this.thumbs?.querySelectorAll('[data-design="true"]').forEach((t) => { t.hidden = t !== thumb; });
      this.track.prepend(slide);
      if (thumb) this.thumbs.prepend(thumb);
      const img = slide.querySelector('img');
      if (img) img.loading = 'eager';
    }
    this.index = 0;
    this.track.scrollLeft = 0;
    this.sync();
  }

  zoom(btn) {
    if (!this.zoomDialog) return;
    const img = btn.querySelector('img');
    const body = this.zoomDialog.querySelector('[data-rdc-zoom-body]');
    body.innerHTML = '';
    this.zoomDialog.removeAttribute('data-zoom');
    this.zoomHint(body.dataset.labelIn);
    if (img) {
      const big = img.cloneNode();
      big.removeAttribute('fetchpriority');
      big.loading = 'eager';
      big.sizes = '(min-width: 768px) 1400px, 220vw'; // sharp enough for the zoomed state
      big.className = 'rdc-zoom__img';
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'rdc-zoom__btn';
      toggle.setAttribute('data-rdc-zoom-toggle', '');
      toggle.setAttribute('aria-pressed', 'false');
      if (body.dataset.labelIn) toggle.setAttribute('aria-label', body.dataset.labelIn);
      toggle.append(big);
      body.append(toggle);
    }
    openDialog(this.zoomDialog, btn);
    track('gallery_zoom_opened', { image_index: this.index + 1 });
  }

  zoomHint(label) {
    const hint = this.zoomDialog.querySelector('[data-rdc-zoom-hint]');
    if (hint && label) hint.textContent = label;
  }

  // Tap or click toggles 2.2x; the tapped point stays in view (keyboard activation has no pointer position: centre)
  toggleZoom(toggle, e) {
    const body = toggle.parentElement;
    const r = toggle.getBoundingClientRect();
    const pointer = e.detail > 0 && r.width > 0 && r.height > 0;
    const fx = pointer ? (e.clientX - r.left) / r.width : 0.5;
    const fy = pointer ? (e.clientY - r.top) / r.height : 0.5;
    const on = this.zoomDialog.toggleAttribute('data-zoom');
    toggle.setAttribute('aria-pressed', String(on));
    const label = on ? body.dataset.labelOut : body.dataset.labelIn;
    if (label) toggle.setAttribute('aria-label', label);
    this.zoomHint(label);
    if (on) {
      body.scrollLeft = fx * toggle.offsetWidth - body.clientWidth / 2;
      body.scrollTop = fy * toggle.offsetHeight - body.clientHeight / 2;
    }
  }
}
customElements.define('rdc-gallery', RdcGallery);

class RdcProduct extends HTMLElement {
  connectedCallback() {
    const map = this.querySelector('[data-rdc-variants]');
    const strings = this.querySelector('[data-rdc-product-strings]');
    this.form = this.querySelector('form[data-rdc-product-form]');
    if (!map || !this.form) return;
    this.variants = JSON.parse(map.textContent);
    this.t = strings ? JSON.parse(strings.textContent) : {};
    this.idInput = this.form.querySelector('[data-rdc-variant-id]');
    this.gallery = this.querySelector('rdc-gallery');
    this.current = this.variants.find((v) => String(v.id) === this.idInput.value) || this.variants[0];
    this.busy = false;

    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-rdc-option-input]');
      if (!input) return;
      this.onOptionChange(Number(input.dataset.rdcOptionInput));
    });
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.querySelector('[data-rdc-sticky-atc]')?.addEventListener('click', (e) => {
      track('sticky_atc_clicked', { variant_id: this.current?.id });
      if (this.fitBlocked) { e.preventDefault(); this.announceBlocked(); return; }
      if (!this.validate()) e.preventDefault();
    });
    this.setupSticky();
    this.refreshAvailability();
    this.setupFitment();
  }

  /* ---------------------------------------------------------------- MP08: vehicle context + fitment status on vehicle hardware.
     Rendering is the block's inline renderer (window.rdcFitRender: a lookup in the published Phase 3A index, never inference).
     This controller only wires state into the form: the editable vehicle note is prefilled, hidden line-item properties carry the
     canonical vehicle (never a plate) and only a completed lookup writes a status; a verified no_fit blocks the add-to-cart for
     that vehicle until the customer switches or explicitly orders for another car. */
  setupFitment() {
    this.fit = this.querySelector('[data-rdc-fitment][data-rdc-fit-vehicle-mode]');
    if (!this.fit || typeof window.rdcFitRender !== 'function' || !window.rdc.vehicle) return;
    this.fitOpts = {};
    this.note = this.form.querySelector('[data-rdc-vehicle]');
    const garage = window.rdc.vehicle.garage;
    this.applyFitment();
    document.addEventListener('rdc:garage-change', () => { this.fitOpts = {}; this.applyFitment(); });
    this.fit.addEventListener('click', (e) => {
      if (e.target.closest('[data-rdc-fit-dismiss]')) { this.fitOpts = { dismissedMismatch: true }; this.applyFitment(); this.fit.querySelector('[data-rdc-fit-choose]')?.focus(); }
      if (e.target.closest('[data-rdc-fit-other]')) { this.fitOpts = { vehicle: null, override: true }; this.applyFitment(); this.note?.focus(); }
      const support = e.target.closest('[data-rdc-fit-support]');
      if (support && this.fitResult?.state === 'unknown') track('fitment_unknown_support_clicked', { hardware_handle: this.fit.dataset.hw });
    });
    this.fit.addEventListener('change', (e) => {
      const input = e.target.closest('[data-rdc-fit-answer]');
      if (!input || this.fitResult?.state !== 'conditional') return;
      const data = this.fit.__rdcData;
      const key = this.fitResult.rel.c;
      const out = this.fit.querySelector('[data-rdc-fit-answer-result]');
      const text = input.value === 'yes' ? data.t.conditionYes.replace('%V', this.fitResult.vehicle.display)
        : input.value === 'no' ? data.t.conditionNo : data.t.conditionUnsure;
      out.textContent = text;
      this.fit.dataset.answer = input.value;
      this.setProp('fitment_condition', `${key}:${input.value}`);
      track('fitment_condition_answered', { condition_key: key, answer: input.value });
    });
    this.note?.addEventListener('input', () => this.syncFitProps());
    // an active vehicle must still exist in the published catalog (tampered or retired handles are dropped, redirects applied)
    if (garage.active()) {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
      idle(() => window.rdc.vehicle.loadModule().then((m) => m.validateGarage()).catch(() => {}));
    }
  }

  applyFitment() {
    const r = window.rdcFitRender(this.fit, this.fitOpts);
    this.fitResult = r;
    const vehicleState = ['unknown', 'fits', 'conditional', 'no_fit', 'unavailable'].includes(r.state) && r.vehicle;
    // editable note: fill only when empty or still our own earlier value; never overwrite what the customer typed
    if (this.note) {
      const auto = this.note.dataset.rdcAutofill;
      if (vehicleState && (!this.note.value.trim() || this.note.value === auto)) {
        this.note.value = r.vehicle.display;
        this.note.dataset.rdcAutofill = r.vehicle.display;
        this.note.removeAttribute('aria-invalid');
        const err = this.form.querySelector('[data-rdc-vehicle-error]');
        if (err) err.hidden = true;
      } else if (!vehicleState && auto && this.note.value === auto) {
        this.note.value = '';
        delete this.note.dataset.rdcAutofill;
      }
    }
    const radios = this.fit.querySelectorAll('[data-rdc-fit-answer]');
    radios.forEach((i) => { i.checked = false; });
    const out = this.fit.querySelector('[data-rdc-fit-answer-result]');
    if (out) out.textContent = '';
    delete this.fit.dataset.answer;
    this.setProp('fitment_condition', null);
    this.fitBlocked = r.state === 'no_fit';
    this.querySelectorAll('[data-rdc-atc]').forEach((btn) => {
      if (this.fitBlocked) { btn.setAttribute('aria-disabled', 'true'); btn.dataset.rdcFitBlocked = 'true'; }
      else if (btn.dataset.rdcFitBlocked) { delete btn.dataset.rdcFitBlocked; if (this.current?.a) btn.removeAttribute('aria-disabled'); }
    });
    this.syncFitProps();
    if (['unknown', 'fits', 'conditional', 'no_fit'].includes(r.state) && this.lastShown !== `${r.state}|${r.vehicle.handle}`) {
      this.lastShown = `${r.state}|${r.vehicle.handle}`;
      track('fitment_status_shown', { status: r.status, hardware_handle: this.fit.dataset.hw });
    }
  }

  /* Hidden properties are true statements only: the canonical vehicle while the (visible, editable) note still names it, and a
     status only when the index lookup actually ran for this vehicle and this hardware. */
  syncFitProps() {
    const r = this.fitResult;
    const data = this.fit.__rdcData;
    const lookup = r && ['unknown', 'fits', 'conditional', 'no_fit'].includes(r.state);
    const associated = r && r.vehicle && ['unknown', 'fits', 'conditional', 'no_fit', 'unavailable'].includes(r.state) && this.noteNames(r.vehicle);
    this.setProp('vehicle_handle', associated ? r.vehicle.handle : null);
    this.setProp('fitment_status', associated && lookup ? r.status : null);
    this.setProp('fitment_index_version', associated && lookup ? data.indexVersion : null);
    if (!associated) this.setProp('fitment_condition', null);
  }

  noteNames(vehicle) {
    if (!this.note) return true;
    const norm = (s) => s.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
    if (this.note.value === vehicle.display) return true;
    const make = window.rdc.vehicle.garage.makeName(vehicle.handle.split('/')[0]);
    const tokens = vehicle.display.slice(make.length).trim().split(/\s+/);
    const model = /^\d+$/.test(tokens[0]) && tokens[1] ? `${tokens[0]}${tokens[1]}` : tokens[0];
    return norm(this.note.value).includes(norm(model || ''));
  }

  setProp(name, value) {
    const input = this.fit?.querySelector(`[data-rdc-prop="${name}"]`);
    if (!input) return;
    if (value == null || value === '') { input.value = ''; input.disabled = true; }
    else { input.value = value; input.disabled = false; }
  }

  selectedOptions() {
    const out = [...this.current.o];
    this.querySelectorAll('[data-rdc-option-input]:checked').forEach((input) => {
      out[Number(input.dataset.rdcOptionInput) - 1] = input.value;
    });
    return out;
  }

  onOptionChange(index) {
    const opts = this.selectedOptions();
    const variant = this.variants.find((v) => v.o.every((val, i) => val === opts[i]));
    this.current = variant || { ...this.current, id: null, o: opts, a: false };
    this.render(variant, index);
    if (variant) {
      if (index === 1) track('projection_selected', { design_code: variant.dc, design_index: this.optionIndex(1, opts[0]), brand: this.dataset.brand, product_family: this.dataset.family, input: 'rail' });
      if (index === 2) track('pack_size_selected', { pack_quantity: variant.q });
    }
  }

  optionIndex(pos, value) {
    const inputs = [...this.querySelectorAll(`[data-rdc-option-input="${pos}"]`)];
    return inputs.findIndex((i) => i.value === value) + 1;
  }

  render(variant, changedIndex) {
    const soldOut = !variant || !variant.a;
    if (variant) this.idInput.value = variant.id;
    // price + stock + labels
    this.querySelectorAll('#ProductPrice-rdc, .rdc-buy__price').forEach((node) => {
      if (variant) node.outerHTML = variant.p.replace('<span class="rdc-price ', `<span${node.id ? ` id="${node.id}"` : ''} class="rdc-price ${node.id ? '' : 'rdc-buy__price '}`);
    });
    const stock = this.querySelector('[data-rdc-stock]');
    if (stock) {
      stock.className = `rdc-stock rdc-stock--${variant ? variant.s : 'out'}`;
      stock.textContent = variant ? variant.st : this.t.unavailable;
    }
    const name = this.querySelector('[data-rdc-selected-name]');
    if (name && variant && variant.d) name.textContent = variant.d;
    const config = this.querySelector('[data-rdc-config]');
    if (config && variant) config.textContent = variant.c;
    const sticky = this.querySelector('[data-rdc-sticky]');
    if (sticky && variant) {
      sticky.querySelector('[data-rdc-sticky-config]').textContent = variant.c || '';
      const tmp = document.createElement('div');
      tmp.innerHTML = variant.p;
      sticky.querySelector('[data-rdc-sticky-price]').textContent = tmp.textContent.trim();
    }
    // add to cart state
    this.querySelectorAll('[data-rdc-atc]').forEach((btn) => {
      if (soldOut || this.fitBlocked) btn.setAttribute('aria-disabled', 'true'); else btn.removeAttribute('aria-disabled');
      btn.querySelector('[data-rdc-atc-label]').textContent = variant ? (variant.a ? this.t.add : this.t.soldOut) : this.t.unavailable;
    });
    const dyn = this.querySelector('[data-rdc-dynamic]');
    if (dyn) dyn.hidden = soldOut;
    this.hideError();
    // media + url
    if (variant && changedIndex === 1 && variant.m && this.gallery) this.gallery.showDesignMedia(variant.m);
    if (variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }
    this.refreshAvailability();
    if (variant) announce(`${variant.c || ''} ${variant.st}`.trim());
  }

  /* Tiles stay selectable when sold out (they are announced as sold out), per MP05 B5 states. */
  refreshAvailability() {
    const opts = this.selectedOptions();
    this.querySelectorAll('[data-rdc-option-input]').forEach((input) => {
      const pos = Number(input.dataset.rdcOptionInput) - 1;
      const probe = [...opts];
      probe[pos] = input.value;
      const v = this.variants.find((x) => x.o.every((val, i) => val === probe[i]));
      const label = input.closest('label');
      const available = !!(v && v.a);
      label.classList.toggle('rdc-tile--out', !available);
      const state = label.querySelector('[data-rdc-tile-state]');
      if (state) state.textContent = available ? '' : this.t.soldOut;
      if (pos === 1 && v) {
        const price = label.querySelector('[data-rdc-pack-price]');
        if (price) { const tmp = document.createElement('div'); tmp.innerHTML = v.p; price.textContent = tmp.textContent.trim(); }
      }
    });
  }

  validate() {
    const field = this.form.querySelector('[data-rdc-vehicle]');
    if (field && field.required && !field.value.trim()) {
      const err = this.form.querySelector('[data-rdc-vehicle-error]');
      field.setAttribute('aria-invalid', 'true');
      if (err) err.hidden = false;
      field.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      field.focus({ preventScroll: true });
      announce(this.t.required);
      return false;
    }
    if (field) {
      field.removeAttribute('aria-invalid');
      const err = this.form.querySelector('[data-rdc-vehicle-error]');
      if (err) err.hidden = true;
    }
    return true;
  }

  announceBlocked() {
    const data = this.fit?.__rdcData;
    const box = this.querySelector('[data-rdc-form-error]');
    if (box && data) { box.textContent = data.t.blocked; box.hidden = false; }
    announce(data ? data.t.blocked : '');
    this.fit?.querySelector('[data-rdc-fit-other]')?.focus();
  }

  hideError() {
    const box = this.querySelector('[data-rdc-form-error]');
    if (box) { box.hidden = true; box.textContent = ''; }
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.busy) return;
    const buttons = [...this.querySelectorAll('[data-rdc-atc]')];
    if (!this.current || !this.current.id || !this.current.a) return;
    if (this.fitBlocked) { this.announceBlocked(); return; }
    if (!this.validate()) return;
    this.busy = true;
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    const surface = e.submitter && e.submitter.hasAttribute('data-rdc-sticky-atc') ? 'sticky' : 'inline';
    try {
      await addToCart(this.form);
      track('add_to_cart', { variant_id: this.current.id, design_code: this.current.dc, pack_quantity: this.current.q, surface });
      announce(this.t.added);
      buttons.forEach((b) => { b.querySelector('[data-rdc-atc-label]').textContent = this.t.added; });
      setTimeout(() => buttons.forEach((b) => { b.querySelector('[data-rdc-atc-label]').textContent = this.current.a ? this.t.add : this.t.soldOut; }), 2000);
      const drawer = document.getElementById('rdc-cart');
      if (drawer) openDialog(drawer, e.submitter || buttons[0]);
    } catch (err) {
      const box = this.querySelector('[data-rdc-form-error]');
      if (box) { box.textContent = err.message || RDC.strings.addError; box.hidden = false; }
      track('add_to_cart_failed', { reason: 'api_error' });
    } finally {
      this.busy = false;
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  }

  /* Mobile sticky CTA: only after the inline buy block has scrolled past the top of the viewport (MP05 §56). */
  setupSticky() {
    const sticky = this.querySelector('[data-rdc-sticky]');
    const buy = this.querySelector('.rdc-buy');
    if (!sticky || !buy) return;
    const mq = matchMedia('(max-width: 1023px)');
    let shownOnce = false;
    let ticking = false;
    const update = () => {
      ticking = false;
      const banner = document.getElementById('shopify-pc__banner');
      const bannerVisible = banner && banner.getBoundingClientRect().height > 0 && getComputedStyle(banner).display !== 'none' && getComputedStyle(banner).visibility !== 'hidden';
      // visible only once the whole inline buy block has left the top of the viewport (also after fast flings and anchor jumps)
      const show = mq.matches && buy.getBoundingClientRect().bottom < 0 && !bannerVisible;
      if (sticky.hidden === !show) return;
      sticky.hidden = !show;
      document.body.classList.toggle('rdc-has-sticky', show);
      if (show && !shownOnce) { shownOnce = true; track('sticky_atc_shown', { scroll_depth_pct: Math.round((window.scrollY / Math.max(1, document.body.scrollHeight - innerHeight)) * 100) }); }
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    mq.addEventListener('change', update);
    update();
  }
}
customElements.define('rdc-product', RdcProduct);
